import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/kit";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import type { SalesByDayRowDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

const chartConfig = {
  revenue: {
    label: "ຍອດຂາຍ",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

type SalesChartProps = {
  rows?: SalesByDayRowDTO[];
  isLoading?: boolean;
  compact?: boolean;
  title?: string;
};

export function SalesChart({
  rows = [],
  isLoading,
  compact = false,
  title = "ຍອດຂາຍຕາມວັນ",
}: SalesChartProps) {
  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          "w-full rounded-2xl",
          compact ? "h-[200px]" : "h-[280px]",
        )}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className={cn(
          "brand-card flex items-center justify-center p-6 text-muted-foreground text-sm",
          compact ? "h-[200px]" : "h-[280px]",
        )}
      >
        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
      </div>
    );
  }

  const data = rows.map((r) => ({
    date: r.date.slice(5),
    revenue: Number(r.revenue),
    orderCount: r.orderCount,
  }));

  return (
    <div className="brand-card min-w-0 overflow-hidden p-4 sm:p-5">
      <h2
        className={cn("mb-4 font-display", compact ? "text-base" : "text-lg")}
      >
        {title}
      </h2>
      <ChartContainer
        config={chartConfig}
        className={cn(
          "aspect-auto w-full min-w-0",
          compact ? "h-[160px]" : "h-[240px]",
        )}
      >
        <BarChart data={data} margin={{ left: 0, right: 4, top: 8, bottom: 0 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
            width={32}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => (
                  <div className="flex flex-col gap-1">
                    <span>{formatLak(String(value))}</span>
                    <span className="text-muted-foreground text-xs">
                      {item.payload.orderCount} ອໍເດີ້
                    </span>
                  </div>
                )}
              />
            }
          />
          <Bar
            dataKey="revenue"
            fill="var(--color-revenue)"
            radius={[6, 6, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
